import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type { DeckorateCommand, RankedCommand } from "../../../types";
import { filterAndRank } from "../services/catalog";
import { logEvent } from "../../../../../telemetry";

/**
 * The two command kinds that both CommandPalette.tsx and AllActionsDialogApp.tsx
 * short-circuit on before their kind-specific run logic: "pending" just surfaces
 * a notice, "ai-preview" swaps in AiPreviewPanel instead of running anything.
 * Returns whether `cmd` was one of these (and thus already handled).
 */
export function handleSharedCommandKinds(
  cmd: DeckorateCommand,
  showNotice: (message: string) => void,
  setAiCommand: (cmd: DeckorateCommand) => void,
): boolean {
  if (cmd.kind === "pending") {
    showNotice(`"${cmd.label}" isn't wired up yet — needs backend/tool integration.`);
    return true;
  }
  if (cmd.kind === "ai-preview") {
    setAiCommand(cmd);
    return true;
  }
  return false;
}

const RECENT_KEY = "deckorate.cmdPalette.recent";
const RECENT_MAX = 12;
const FAVORITES_KEY = "deckorate.cmdPalette.favorites";

/**
 * CommandPalette storage operations belong to the Deckorate connector; tag
 * every telemetry event with this `connectorSystemName` so they slice under
 * the "integration" backend (matches colourOps.ts's / CommandPalette.tsx's
 * convention — each file declares its own copy of this same literal rather
 * than sharing an import, consistent with how COLOR_CONNECTOR/MAP_CONNECTOR/
 * CMD_PALETTE_CONNECTOR are each declared locally in their own file).
 */
const CMD_PALETTE_CONNECTOR = "deckorate";

// Named so every reportStorageError call site references one of these
// instead of repeating its own copy of the string literal.
const STORAGE_REASON_READ_ERROR = "cmd_palette_read_error"; // getItem/JSON.parse threw
const STORAGE_REASON_READ_INVALID = "cmd_palette_read_invalid"; // parsed fine, wrong shape
const STORAGE_REASON_WRITE = "cmd_palette_write"; // setItem threw

/** Reports a localStorage read/write failure (private mode, quota, disabled
 * storage, malformed JSON, or well-formed JSON of the wrong shape) without
 * disrupting the caller — recents/favorites are a non-critical enhancement,
 * so both directions still fall back to empty/no-op, but the failure is no
 * longer swallowed silently. This is a `warn`, not an `error`: it's a
 * gracefully-degrading, non-critical failure (same category as
 * CommandPalette.tsx's logCmdPaletteWarn / mapPlotter.ts's per-item-skip
 * calls), not one that aborts an operation — so it's logged via the same
 * catalogued `connector.action.invoke`/`warn` event those use, rather than
 * the error-severity `error.storage.failure` event (whose `error.*` name
 * would misleadingly imply error severity under a `warn` kind).
 *
 * A `warn`-kind event can only carry `reason` from the closed
 * ALLOWED_PROPERTY_KEYS enum (see telemetry/types.ts) — it has no
 * `type`/`scrubbedMessage` block the way an `error`-kind logError call
 * does, so the raw exception text can't be attached here without either
 * promoting this to `error` severity (misrepresenting a non-critical,
 * gracefully-degrading read/write as something worth alerting on) or
 * widening the warn schema repo-wide. `reason` is deliberately split into
 * read-error vs. read-invalid-shape vs. write below so the closed-enum
 * signal itself still distinguishes "storage/parse threw" from "storage
 * returned valid JSON of the wrong shape" — as much diagnostic detail as
 * this schema allows a warn-level event to carry. */
function reportStorageError(
  reason: typeof STORAGE_REASON_READ_ERROR | typeof STORAGE_REASON_READ_INVALID | typeof STORAGE_REASON_WRITE,
): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: CMD_PALETTE_CONNECTOR, reason });
}

// Named so the shape check in readIds reads as one intent-revealing call
// rather than an inline `Array.isArray(...) && ....every(...)` expression.
function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function readIds(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    // Anything that isn't a plain string array — a shape from before a
    // future format change, or a value some other script wrote under this
    // key — is treated the same as "nothing saved yet" rather than thrown:
    // recents/favorites are a non-critical convenience, not worth crashing
    // the palette over.
    if (!isStringArray(parsed)) {
      reportStorageError(STORAGE_REASON_READ_INVALID);
      return [];
    }
    return parsed;
  } catch (e) {
    // getItem can throw (storage disabled in private/incognito mode) and
    // JSON.parse can throw on malformed JSON. reportStorageError's `reason`
    // can't carry `e`'s own text (see its doc comment for why), so it's
    // logged to the console instead — matching brandApplier.ts's
    // console.debug/warn + errorMessage(e) convention for exception detail
    // that telemetry's warn-kind events have no room for.
    console.debug("[useCommandPalette] localStorage read failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_READ_ERROR);
    return [];
  }
}

function writeIds(key: string, ids: readonly string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch (e) {
    // setItem can throw (quota exceeded, or storage disabled) — same
    // console-detail / closed-enum-reason split as readIds' catch above.
    console.debug("[useCommandPalette] localStorage write failed:", e instanceof Error ? e.message : String(e));
    reportStorageError(STORAGE_REASON_WRITE);
  }
}

export function getRecentIds(): string[] {
  return readIds(RECENT_KEY);
}

export function saveRecentId(id: string): void {
  const next = [id, ...getRecentIds().filter((existingId) => existingId !== id)].slice(0, RECENT_MAX);
  writeIds(RECENT_KEY, next);
}

export function getFavoriteIds(): string[] {
  return readIds(FAVORITES_KEY);
}

/** Toggles `id` in the favorites list (most-recent-first, uncapped) and
 * returns the updated list so callers can sync their state in one step. */
export function toggleFavoriteId(id: string): string[] {
  const current = getFavoriteIds();
  const next = current.includes(id) ? current.filter((existingId) => existingId !== id) : [id, ...current];
  writeIds(FAVORITES_KEY, next);
  return next;
}

interface UseCommandPaletteResult {
  readonly isOpen: boolean;
  readonly open: () => void;
  readonly close: () => void;
}

/**
 * Installs the Ctrl/Cmd+Shift+K listener that toggles the palette. Uses a
 * capture-phase document listener (not a React synthetic handler) so it
 * intercepts the keystroke regardless of which element inside the panel has
 * focus — same approach as the DecKorate POC's commandPalette.js.
 */
export function useCommandPalette(): UseCommandPaletteResult {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isCmdOrCtrl = e.ctrlKey || e.metaKey;
      if (!isCmdOrCtrl || !e.shiftKey) {
        return;
      }
      if (e.key !== "k" && e.key !== "K") {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsOpen((v) => !v);
    }
    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, []);

  return { isOpen, open, close };
}

interface UseActionSearchResult {
  readonly query: string;
  readonly setQuery: (query: string) => void;
  readonly activeIndex: number;
  readonly setActiveIndex: (index: number) => void;
  readonly results: RankedCommand[];
  readonly move: (dir: number) => void;
  readonly toggleFavorite: (id: string) => void;
  readonly recordRecent: (id: string) => void;
}

/**
 * Shared search/list state behind both CommandPalette.tsx (in-pane overlay)
 * and AllActionsDialogApp.tsx (Office Dialog): query -> fuzzy-ranked results,
 * the highlighted row, and localStorage-backed recents/favorites.
 */
export function useActionSearch(): UseActionSearchResult {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>(() => getRecentIds());
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => getFavoriteIds());

  const results = useMemo(
    () => filterAndRank(query, recentIds, favoriteIds),
    [query, recentIds, favoriteIds],
  );

  // Reset activeIndex during render (not a useEffect) so it's never one frame
  // stale vs. the just-recomputed `results` for the new query.
  // See https://react.dev/learn/you-might-not-need-an-effect
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setActiveIndex(0);
  }

  const move = useCallback(
    (dir: number) => {
      setActiveIndex((idx) => {
        if (results.length === 0) return 0;
        return (idx + dir + results.length) % results.length;
      });
    },
    [results.length],
  );

  const toggleFavorite = useCallback((id: string) => {
    setFavoriteIds(toggleFavoriteId(id));
  }, []);

  const recordRecent = useCallback((id: string) => {
    saveRecentId(id);
    setRecentIds(getRecentIds());
  }, []);

  return { query, setQuery, activeIndex, setActiveIndex, results, move, toggleFavorite, recordRecent };
}

interface UsePaletteChromeResult {
  readonly notice: string | null;
  readonly showNotice: (message: string) => void;
}

/**
 * Shared UI chrome behind both CommandPalette.tsx (in-pane overlay) and
 * AllActionsDialogApp.tsx (Office Dialog)'s search view:
 * - focuses the input on mount
 * - closes on an outside mousedown, without a handler on the role="dialog"
 *   overlay itself (that pairing trips "avoid non-native interactive
 *   elements" / "must have a keyboard listener" lint rules)
 * - traps Tab inside the panel: this is a plain <dialog open>, not a
 *   showModal() one, so nothing stops focus leaving the panel natively, and
 *   Tab is repurposed at the input to run the highlighted command instead
 * - manages a self-dismissing notice for command failures
 */
export function usePaletteChrome(
  panelRef: RefObject<HTMLDivElement>,
  inputRef: RefObject<HTMLInputElement>,
  onClose: () => void,
): UsePaletteChromeResult {
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  useEffect(() => {
    return () => {
      if (noticeTimeoutRef.current !== null) window.clearTimeout(noticeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    function handleOutsideMouseDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () => document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [panelRef, onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || e.target === inputRef.current) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    panel.addEventListener("keydown", handleKeyDown);
    return () => panel.removeEventListener("keydown", handleKeyDown);
  }, [panelRef, inputRef]);

  const showNotice = useCallback((message: string) => {
    if (noticeTimeoutRef.current !== null) window.clearTimeout(noticeTimeoutRef.current);
    setNotice(message);
    noticeTimeoutRef.current = window.setTimeout(() => setNotice(null), 3000);
  }, []);

  return { notice, showNotice };
}

/**
 * Closes on Escape while `active`, via a document-level listener rather than
 * an input's onKeyDown — used by the ai-preview panel (CommandPalette.tsx /
 * AllActionsDialogApp.tsx), which replaces the search input entirely.
 */
export function useEscapeToClose(active: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!active) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [active, onClose]);
}
